import PropTypes from 'prop-types';
import React, { useCallback } from 'react';
import {
  Grid,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  DialogActions,
  TextField,
  Typography,
  Button,
  Tooltip,
} from '@mui/material';
import { useFormik } from 'formik';
import { object, string, array } from 'yup';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import REGEX from '../const/Regex';
import DialogTransition from './Common/DialogTransition';
import MultiSelect from './Common/MultiSelect';
import { ReactComponent as CloseIcon } from '../assets/CloseIcon.svg';

/**
 * @component GroupDialog
 * @description GroupDialog - React component for adding a group.
 * @param {Object} props - The props object containing the component's data and functions.
 * @param {boolean} props.open - Whether the dialog is open.
 * @param {Function} props.setOpen - Function to set the open state of the dialog.
 * @param {Function} props.createGroup - Function to create a new group.
 * @param {string} props.orgName - The organization ID.
 * @param {Function} props.setTriggerGrouplist - Function to trigger group list update.
 * @returns {JSX.Element} React component.
 */

const GroupDialog = ({
  open,
  setOpen,
  createGroup,
  orgName,
  setTriggerGrouplist,
  client,
  userList,
  setSelectedItem,
}) => {
  const initialValues = {
    name: '',
    users: [],
    description: '',
  };

  /**
   * Validation schema for the form fields.
   * @typedef {Object} ValidationSchema
   * @property {string} name - The name field validation rules.
   * @property {string} description - The description field validation rules.
   */

  const validationSchema = object({
    name: string()
      .required('Required')
      .min(2, 'Group name must be at least 2 characters')
      .matches(REGEX.DISPLAY_NAME, 'Invalid group name')
      .max(150, 'Group name must be at most 150 characters'),
    users: array(),
    // users: array().min(1, "At least one user must be selected").required("Required"),
    description: string()
      .optional()
      .min(2, 'Description must be at least 2 characters')
      .max(320, 'Description must be at most 320 characters'),
  });

  /**
   * Formik form object for managing form state and submission.
   * @typedef {Object} FormikForm
   * @property {Object} initialValues - Initial values for form fields.
   * @property {Object} validationSchema - Validation schema for form fields.
   * @property {Function} onSubmit - Function to handle form submission.
   * @property {Function} handleReset - Function to reset form fields.
   */

  const formikForm = useFormik({
    initialValues,
    validationSchema,
    onSubmit: async (formData) => {
      const values = {
        ...formData,
        users: formData?.users?.map((user) => user?.value),
        product_id: client?.id,
      };
      try {
        await createGroup(values, orgName);
        setTriggerGrouplist(true);
        setSelectedItem(null);
      } catch (error) {
        console.error('Error creating group:', error);
      }
      setOpen(false);
      formikForm.handleReset();
    },
  });

  const handleChange = useCallback(
    (e) => formikForm.handleChange(e),
    [formikForm]
  );
  const handleOnClose = useCallback(() => {
    setOpen((prev) => !prev);
    formikForm.handleReset();
  }, [formikForm, setOpen]);

  return (
    <Dialog
      maxWidth="xs"
      fullWidth={true}
      onClose={handleOnClose}
      TransitionComponent={DialogTransition}
      open={open}
    >
      <DialogTitle>{client?.label}: Create Group </DialogTitle>
      <IconButton
        color="primary"
        aria-label="Close dialog"
        onClick={handleOnClose}
        sx={{ position: 'absolute', right: 16, top: 8 }}
      >
        <CloseIcon />
      </IconButton>
      <DialogContent dividers>
        <Grid container direction="row" spacing={2}>
          <Grid item xs={12}>
            <TextField
              label="Group name"
              required
              placeholder="Enter name"
              name="name"
              value={formikForm.values.name}
              onChange={handleChange}
              aria-label="name"
              onBlur={formikForm.handleBlur}
              helperText={
                formikForm?.errors?.name && formikForm?.touched?.name
                  ? formikForm?.errors?.name
                  : null
              }
              error={
                Boolean(formikForm?.errors?.name) && formikForm?.touched?.name
              }
              InputProps={{
                endAdornment: (
                  <Tooltip
                    title={
                      <Typography>
                        - Group name should start with a character. <br />
                        - Numbers (0-9) are allowed. <br />- Underscore(_) and
                        hyphen (-) are allowed. <br />- Spaces are not allowed.
                      </Typography>
                    }
                    arrow
                    placement="top"
                  >
                    <InfoOutlinedIcon />
                  </Tooltip>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <MultiSelect
              label="Users"
              multiple
              placeholder="Select users"
              name="users"
              value={formikForm?.values?.users}
              onChange={(e) => formikForm.setFieldValue('users', e)}
              noOptionsText="No users available."
              limitTags={3}
              aria-label="users"
              onBlur={formikForm?.handleBlur}
              helperText={
                formikForm?.errors?.users && formikForm?.touched?.users
                  ? formikForm?.errors?.users
                  : null
              }
              error={
                Boolean(formikForm?.errors?.users) && formikForm?.touched?.users
              }
              options={
                userList?.length > 0
                  ? userList?.map((item) => {
                      return { label: item?.username, value: item?.id };
                    })
                  : []
              }
              getOptionLabel={(option) => {
                return option?.label ? option?.label : '';
              }}
              isOptionEqualToValue={(option, val) =>
                option?.value === val?.value
              }
              renderOption={(props, option) => (
                <Box
                  display="flex"
                  flexDirection="row"
                  flexGrow={1}
                  alignItems="center"
                  {...props}
                  minHeight="unset !important"
                  height="unset !important"
                  key={option?.value}
                >
                  <Box display="flex" flexGrow={1} flexDirection="column">
                    <Typography variant="body2">{`${option?.label}`}</Typography>
                  </Box>
                </Box>
              )}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Description"
              placeholder="Enter description"
              name="description"
              value={formikForm.values.description}
              onChange={formikForm.handleChange}
              onBlur={formikForm.handleBlur}
              error={
                formikForm.touched.description &&
                Boolean(formikForm.errors.description)
              }
              helperText={
                formikForm.touched.description && formikForm.errors.description
              }
              fullWidth
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button
          variant="outlined"
          onClick={formikForm.handleReset}
          title="Reset"
        >
          Reset
        </Button>
        <Button variant="contained" onClick={formikForm.handleSubmit}>
          Submit
        </Button>
      </DialogActions>
    </Dialog>
  );
};

GroupDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  setOpen: PropTypes.func.isRequired,
  createGroup: PropTypes.func.isRequired,
  orgName: PropTypes.string.isRequired,
  setTriggerGrouplist: PropTypes.func.isRequired,
  setSelectedItem: PropTypes.func.isRequired,
  client: PropTypes.oneOfType([PropTypes.object]).isRequired,
  userList: PropTypes.oneOfType([PropTypes.object]).isRequired,
};

export default GroupDialog;
